/**
 * Page size for `fetchAllRows`. Matches `max_rows` in the API's `supabase/config.toml`: Supabase never returns
 * more rows than that in one request, so a larger page size wouldn't fetch more per request.
 */
export const SUPABASE_MAX_ROWS = 1000;

type PageResult<T> = { data: T[] | null; error: { message: string } | null; count?: number | null };

/**
 * Fetches every row of a query by requesting consecutive pages.
 * `fetchPage` must apply a stable `.order()` and `.range(from, to)`, so pages don't overlap or skip rows.
 * When it also selects with `{ count: "exact" }`, paging stops once that many rows arrived, so it keeps working
 * even if the server's row limit is lower than `SUPABASE_MAX_ROWS`. Without a count, it stops at the first short page.
 */
export async function fetchAllRows<T>(fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>): Promise<T[]> {
  const rows: T[] = [];
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error, count } = await fetchPage(rows.length, rows.length + SUPABASE_MAX_ROWS - 1);
    if (error) throw new Error(error.message);
    const page = data || [];
    rows.push(...page);
    const isLastPage = typeof count === "number" ? rows.length >= count : page.length < SUPABASE_MAX_ROWS;
    if (isLastPage || page.length === 0) return rows;
  }
}

/** Splits a list into chunks of at most `size` items. e.g. ([1, 2, 3], 2) → [[1, 2], [3]] */
export function splitIntoChunks<T>(items: T[], size: number): T[][] {
  if (size < 1) throw new Error("Chunk size must be at least 1.");
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/** Runs `fn` for every item, with at most `concurrency` calls in flight at a time. Results keep the input order. */
export async function mapWithConcurrency<T, R>(items: T[], concurrency: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await fn(items[index]);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  return results;
}
